import React, { useState, useMemo, useRef } from 'react';
// @ts-ignore
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
// date picker
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface BookingFormProps {
    packageId: string;
    packageTitle?: string;
    price?: number;
}

export const BookingForm: React.FC<BookingFormProps> = ({ packageId, packageTitle, price }) => {
    const [name, setName] = useState('');
    const [city, setCity] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [travelDestination, setTravelDestination] = useState(packageTitle || '');
    const [dateOfTravel, setDateOfTravel] = useState<Date | null>(null);
    const [noOfPeople, setNoOfPeople] = useState<number>(2);
    const [vacationType, setVacationType] = useState('Honeymoon');

    // Simple math captcha
    const captcha = useMemo(() => {
        const a = Math.floor(Math.random() * 8) + 1; // 1-8
        const b = Math.floor(Math.random() * 8) + 1;
        return { a, b, sum: a + b };
    }, []);
    const [captchaAnswer, setCaptchaAnswer] = useState('');

    const dateWrapperRef = useRef<HTMLDivElement | null>(null);

    const onCalendarOpen = () => {
        // ensure popper matches input width shortly after it opens
        setTimeout(() => {
            try {
                const popper = document.querySelector('.react-datepicker__popper') as HTMLElement | null;
                const wrapper = dateWrapperRef.current;
                if (popper && wrapper) {
                    const w = wrapper.getBoundingClientRect().width;
                    popper.style.minWidth = `${w}px`;
                }
            } catch (e) {
                // ignore
            }
        }, 10);
    };

    const handleSubmit = () => {
        if (!name.trim() || !city.trim() || !email.trim() || !phone.trim() || !travelDestination.trim() || !dateOfTravel || !vacationType.trim()) {
            alert('Please fill all required fields.');
            return;
        }

        if (parseInt(captchaAnswer || '0') !== captcha.sum) {
            alert('Captcha answer is incorrect.');
            return;
        }

        // Submit logic placeholder
        alert('Booking request submitted successfully!');

        // Optionally clear form
        setName('');
        setCity('');
        setEmail('');
        setPhone('');
        setWhatsappNumber('');
        setTravelDestination(packageTitle || '');
        setDateOfTravel(null);
        setNoOfPeople(2);
        setVacationType('Honeymoon');
        setCaptchaAnswer('');
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Enquiry Form</h2>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">City of Residence *</label>
                <input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Phone Number *</label>
                <PhoneInput country={'in'} enableSearch value={phone} onChange={(val) => setPhone(val)} inputProps={{ name: 'phone', required: true }} containerClass="react-phone-input-container" inputClass="w-full" />
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">WhatsApp Number</label>
                <PhoneInput country={'in'} enableSearch value={whatsappNumber} onChange={(val) => setWhatsappNumber(val)} inputProps={{ name: 'whatsapp' }} containerClass="react-phone-input-container" inputClass="w-full" />
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Travel Destination *</label>
                <input value={travelDestination} onChange={(e) => setTravelDestination(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
            </div>

            <div className="mb-3" ref={dateWrapperRef}>
                <label className="block text-sm font-medium text-gray-700">Date of Travel *</label>
                <DatePicker
                    selected={dateOfTravel}
                    onChange={(d) => setDateOfTravel(d)}
                    onCalendarOpen={onCalendarOpen}
                    minDate={new Date()}
                    placeholderText={"Select travel date *"}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    dateFormat="yyyy-MM-dd"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                />
                <style>{`
                    /* Ensure the input rendered by react-datepicker stretches full width like other inputs */
                    .react-datepicker-wrapper {
                        display: block !important;
                        width: 100% !important;
                    }

                    .react-datepicker__input-container {
                        display: block !important;
                        width: 100% !important;
                    }

                    .react-datepicker__input-container input {
                        width: 100% !important;
                        box-sizing: border-box;
                    }

                    /* mark disabled past dates with reduced opacity */
                    .react-datepicker__day--disabled {
                        opacity: 0.5;
                        position: relative;
                    }

                    /* Show centered block icon only when hovering a disabled (past) date cell */
                    .react-datepicker__day--disabled:hover::after {
                        content: "⛔";
                        position: absolute;
                        left: 50%;
                        top: 50%;
                        transform: translate(-50%, -50%);
                        color: #ef4444;
                        font-size: 18px;
                        pointer-events: none;
                        opacity: 0.95;
                    }
                `}</style>
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">No of People *</label>
                <input type="number" min={1} value={noOfPeople} onChange={(e) => setNoOfPeople(parseInt(e.target.value || '1'))} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Vacation Type *</label>
                <select value={vacationType} onChange={(e) => setVacationType(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option>Honeymoon</option>
                    <option>Family</option>
                    <option>Group</option>
                    <option>Couple</option>
                    <option>Solo</option>
                    <option>Others</option>
                </select>
            </div>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Captcha *</label>
                <div className="flex items-center gap-2">
                    <div className="px-3 py-2 bg-gray-100 rounded">{captcha.a} + {captcha.b} =</div>
                    <input value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
            </div>

            <button onClick={handleSubmit} className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 transition-colors duration-200 font-medium whitespace-nowrap">SUBMIT</button>
        </div>
    );
};

export default BookingForm;
